import PropTypes from "prop-types";
import { memo, useMemo } from "react";
import classNames from "classnames";

import { boldStyle, normalStyle } from "./modal_children.module.scss";
import { rupeeFontStyle } from "@/assets/styles/common.module.scss";
import { toAmountFloat } from "@/helpers/utils";
import { useTranslation } from "react-i18next";

const num = (v) =>
  v === null || v === undefined || v === "" ? NaN : Number(v);
const isPositiveOrZero = (v) => Number.isFinite(num(v));

const Row = memo(function Row({
  label,
  value,
  bold = false,
  negative = false,
}) {
  const formatted = toAmountFloat(String(value ?? 0));

  return (
    <div className="d-flex align-items-center">
      <span
        className={classNames(bold ? boldStyle : normalStyle, "py-1 small")}
      >
        {label}
      </span>
      <span className="small ms-auto py-1">
        {negative && (
          <span className={bold ? boldStyle : normalStyle}>(-) &nbsp;</span>
        )}
        <span className={bold ? boldStyle : normalStyle}>
          <span className={rupeeFontStyle}>₹&nbsp;</span>
          {formatted}
        </span>
      </span>
    </div>
  );
});

Row.displayName = "HPremiumBreakupModal.Row";

Row.propTypes = {
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  bold: PropTypes.bool,
  negative: PropTypes.bool,
};

Row.defaultProps = {
  value: 0,
  bold: false,
  negative: false,
};

const Divider = () => (
  <div
    className="my-2 d-flex align-self-stretch"
    style={{ borderTop: "1px dashed #bcbcbc" }}
  />
);

const HPremiumBreakupModal = ({ premiumDetails, systemDate, isGroup }) => {
  const {
    basePremium,
    totalRiderPremium,
    floaterDiscount,
    employeeFlagDiscount,
    roomCategoryDiscount,
    policyTermDiscount,
    netPremium,
    sgstVal,
    cgstVal,
    igstVal,
    totalPremium,
    totalGlobalLoading,
    totalDiscountAmount,
    gstValue,
    policyTotalPremium,
    packagePremium,
  } = premiumDetails;

  const { t } = useTranslation();

  const { gst, gstIsZero, gstRateSuffix, inclusiveSuffix } = useMemo(() => {
    const explicitGST = gstValue || gstValue === 0 ? num(gstValue) : NaN;
    const igst = num(igstVal);
    const sPlusC = num(sgstVal) + num(cgstVal);
    const computedGST = Number.isFinite(explicitGST)
      ? explicitGST
      : isPositiveOrZero(igst)
      ? igst
      : sPlusC;

    if (isGroup) {
      return {
        gst: isPositiveOrZero(computedGST) ? computedGST : 0,
        gstIsZero: false,
        gstRateSuffix: ` ${t("r_quote_hpremiumbreakup_health_content_15")}`,
        inclusiveSuffix: t("r_quote_hpremiumbreakup_health_content_16"),
      };
    }

    // Retail: Treat as "zero GST" if gst==0 OR systemDate is on/after 22 Sep 2025
    const cutoff = new Date("2025-09-22T00:00:00.000Z");
    const sysd = systemDate ? new Date(systemDate) : null;
    const onOrAfterCutoff =
      sysd instanceof Date && !isNaN(sysd) && sysd >= cutoff;

    const treatAsZero = Number(computedGST) === 0 || onOrAfterCutoff;

    return {
      gst: treatAsZero ? 0 : computedGST,
      gstIsZero: treatAsZero,
      gstRateSuffix: treatAsZero
        ? ""
        : ` ${t("r_quote_hpremiumbreakup_health_content_15")}`,
      inclusiveSuffix: treatAsZero
        ? ""
        : t("r_quote_hpremiumbreakup_health_content_16"),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gstValue, igstVal, sgstVal, cgstVal, systemDate, isGroup, t]);

  const renderRow = (label, value, { bold = false, negative = false } = {}) => {
    if (!isPositiveOrZero(value)) return null;
    return (
      <Row
        key={label}
        label={label}
        value={value}
        bold={bold}
        negative={negative}
      />
    );
  };

  return (
    <div className="d-flex flex-column mx-4">
      {renderRow(t("r_quote_hpremiumbreakup_health_content_1"), basePremium)}{" "}
      {/* Base premium */}
      {renderRow(
        t("r_quote_hpremiumbreakup_health_content_11"),
        totalGlobalLoading
      )}{" "}
      {/* Global loading */}
      {renderRow(
        t("r_quote_hpremiumbreakup_health_content_10"),
        totalRiderPremium
      )}{" "}
      {/* Rider premium */}
      {num(totalDiscountAmount) > 0 && (
        <Row
          label={t("r_quote_hpremiumbreakup_health_content_12")}
          value={totalDiscountAmount}
          negative
        />
      )}
      {/*
      {renderRow(t("r_quote_hpremiumbreakup_health_content_2"), floaterDiscount, { negative: true })}
      {renderRow(t("r_quote_hpremiumbreakup_health_content_7"), employeeFlagDiscount, { negative: true })}
      {renderRow(t("r_quote_hpremiumbreakup_health_content_8"), roomCategoryDiscount, { negative: true })}
      {renderRow(t("r_quote_hpremiumbreakup_health_content_9"), policyTermDiscount, { negative: true })}
      */}
      {/* Net Premium */}
      <Row
        label={t("r_quote_hpremiumbreakup_health_content_4")}
        value={netPremium}
        bold
      />
      <Divider />
      {/* GST row with conditional suffix and dash display */}
      <Row
        label={`${t(
          "r_quote_hpremiumbreakup_health_content_5"
        )}${gstRateSuffix}`}
        value={gst}
      />
      {/* Policy premium (with conditional "inclusive of GST") */}
      {num(policyTotalPremium) > 0 && (
        <Row
          label={`${t(
            "r_quote_hpremiumbreakup_health_content_13"
          )}${inclusiveSuffix}`}
          value={policyTotalPremium}
        />
      )}
      {/* Package premium (with conditional "inclusive of GST") */}
      {num(packagePremium) > 0 && (
        <Row
          label={`${t(
            "r_quote_hpremiumbreakup_health_content_14"
          )}${inclusiveSuffix}`}
          value={packagePremium}
        />
      )}
      <Divider />
      {/* Total */}
      <Row
        label={t("r_quote_hpremiumbreakup_health_content_6")}
        value={Math.round(num(totalPremium))}
        bold
      />
    </div>
  );
};

HPremiumBreakupModal.propTypes = {
  premiumDetails: PropTypes.object.isRequired,
  systemDate: PropTypes.string,
  isGroup: PropTypes.bool,
};

export default HPremiumBreakupModal;
